import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Tree } from 'antd';
import { FolderOpenOutlined, FolderOutlined, ReloadOutlined } from '@ant-design/icons';
import { getWaveformDirectories } from '../services/api';
import './WaveformDirectoryTree.css';

const rootNode = () => ({ key: '', title: '全部目录', name: '全部目录', relative_path: '', isLeaf: false });
const attachChildren = (nodes, parent, children) => nodes.map(node => {
  if (node.key === parent) return { ...node, children };
  return node.children ? { ...node, children: attachChildren(node.children, parent, children) } : node;
});
const findNode = (nodes, key) => {
  for (const node of nodes) {
    if (node.key === key) return node;
    const child = node.children && findNode(node.children, key);
    if (child) return child;
  }
  return null;
};

export default function WaveformDirectoryTree({ directory, onSelect, refreshedAt, ready = true }) {
  const [nodes, setNodes] = useState(() => [rootNode()]);
  const [expandedKeys, setExpandedKeys] = useState(['']);
  const [loadedKeys, setLoadedKeys] = useState(['']);
  const [errors, setErrors] = useState({});
  const [treeGeneration, setTreeGeneration] = useState(0);
  const generation = useRef(0);
  const cache = useRef(new Set());
  const pending = useRef(new Map());
  const reconciledDirectory = useRef(null);

  const load = useCallback((parent) => {
    if (cache.current.has(parent)) return Promise.resolve();
    if (pending.current.has(parent)) return pending.current.get(parent);
    const requestGeneration = generation.current;
    setErrors(previous => {
      const next = { ...previous };
      delete next[parent];
      return next;
    });
    const request = (async () => {
      try {
        const response = await getWaveformDirectories(parent);
        if (requestGeneration !== generation.current) return;
        const payload = response.data;
        if (!payload.success) throw new Error(payload.message || '获取目录失败');
        const children = payload.data.map(item => ({
          key: item.relative_path, title: item.name, name: item.name, relative_path: item.relative_path,
          isLeaf: !item.has_children,
        }));
        cache.current.add(parent);
        setNodes(previous => attachChildren(previous, parent, children));
      } catch (error) {
        if (requestGeneration !== generation.current) return;
        setErrors(previous => ({ ...previous,
          [parent]: error.response?.data?.message || error.message || '获取目录失败',
        }));
      } finally {
        if (requestGeneration === generation.current) {
          pending.current.delete(parent);
          // Failed nodes wait for explicit retry, avoiding the tree's automatic retry loop.
          setLoadedKeys(previous => previous.includes(parent) ? previous : [...previous, parent]);
        }
      }
    })();
    pending.current.set(parent, request);
    return request;
  }, []);

  useEffect(() => {
    generation.current += 1;
    cache.current = new Set();
    pending.current = new Map();
    reconciledDirectory.current = null;
    setNodes([rootNode()]);
    setExpandedKeys(['']);
    setLoadedKeys(['']);
    setErrors({});
    setTreeGeneration(previous => previous + 1);
    if (ready) load('');
    return () => { generation.current += 1; };
  }, [refreshedAt, ready, load]);

  useEffect(() => {
    if (!directory) {
      reconciledDirectory.current = null;
      return;
    }
    if (!ready || !cache.current.has('') || reconciledDirectory.current === directory) return;
    const paths = [''];
    for (const part of directory.split('/')) paths.push(paths[paths.length - 1] ? `${paths[paths.length - 1]}/${part}` : part);
    const parents = paths.filter(key => {
      const node = findNode(nodes, key);
      return node && !node.isLeaf;
    });
    setExpandedKeys(previous => parents.every(key => previous.includes(key)) ? previous : [...new Set([...previous, ...parents])]);
    parents.forEach(parent => { void load(parent); });
    // Later sibling loads must preserve manual collapse once this selection is visible.
    if (findNode(nodes, directory)) reconciledDirectory.current = directory;
  }, [directory, nodes, ready, load]);

  return (
    <aside className="waveform-directory-panel" aria-label="波形目录">
      <div className="waveform-directory-heading">目录</div>
      {Object.entries(errors).map(([parent, error]) => (
        <Alert key={parent} type="error" showIcon
          title={<span>{parent || '全部目录'}：{error}</span>}
          action={<Button size="small" icon={<ReloadOutlined />} onClick={() => load(parent)}>重试</Button>}
        />
      ))}
      <div className="waveform-directory-scroll">
        <Tree
          key={treeGeneration}
          showIcon
          blockNode
          treeData={nodes}
          selectedKeys={[directory]}
          expandedKeys={expandedKeys}
          loadedKeys={loadedKeys}
          autoExpandParent={false}
          onExpand={setExpandedKeys}
          loadData={node => ready ? load(node.relative_path) : Promise.resolve()}
          onSelect={(_, info) => {
            onSelect(info.node.relative_path);
            if (ready && !info.node.isLeaf) {
              setExpandedKeys(previous => previous.includes(info.node.key) ? previous : [...previous, info.node.key]);
              void load(info.node.relative_path);
            }
          }}
          icon={({ expanded }) => expanded ? <FolderOpenOutlined /> : <FolderOutlined />}
          titleRender={node => <span title={node.relative_path || node.name}>{node.name}</span>}
        />
      </div>
    </aside>
  );
}
